import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Form, Field, useFormikContext } from 'formik';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import {
  DropdownWithValidation,
  FormikCalendar,
  FormikCheckbox,
  FormikMultiSelect,
} from '../../components/prime-formik';
import { renderAsyncSelector } from '../../components/formik-components/FormAsyncSelector';
import FormLanguageSelector from '../../components/formik-components/FormLanguageSelector';
import Debug from '../../components/Debug';
import useSubscriptionExpiredRestrictions from '../../hooks/useSubscriptionExpiredRestrictions';
import { useSelector } from 'react-redux';

const messages = defineMessages({
  selectDefaultText: {
    id: 'actions.select',
    defaultMessage: '- Select -',
  },
  withTargetJobOptionText: {
    id: 'export.respondent.withTargetJob',
    defaultMessage: 'Respondents with Job Profile as Target Job',
  },
  allTargetJobOptionText: {
    id: 'export.respondent.all',
    defaultMessage: 'All respondents possible',
  },
  startDate: {
    id: 'placeholder.startWith',
    defaultMessage: 'Start date',
  },
  endDate: {
    id: 'placeholder.endWith',
    defaultMessage: 'End date',
  },
  generate: { id: 'actions.generate', defaultMessage: 'Generate' },
  choose: {
    id: 'choose',
    defaultMessage: 'Choose',
  },
});

const DataExportForm = (props) => {
  const {
    accounts,
    templates,
    jobSearching,
    jobs,
    searchJob,
    changeURLParams,
    changeURLDateParams,
    changeAsmtTemplate,
  } = props;

  const { translatedTags } = useSelector((state) => state.i18n);
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();
  const { isSubmitting, setFieldValue } = useFormikContext();
  const { formatMessage } = useIntl();

  // define list respondent type
  const RESPONDENT_TYPES = [
    {
      value: '',
      label: formatMessage(messages.selectDefaultText),
    },
    {
      value: 'TARGET_JOB',
      label: formatMessage(messages.withTargetJobOptionText),
    },
    {
      value: 'ALL',
      label: formatMessage(messages.allTargetJobOptionText),
    },
  ];

  const startDateLabel = formatMessage(messages.startDate);
  const endDateLabel = formatMessage(messages.endDate);
  return (
    <Form>
      <div className="p-grid p-fluid">
        <div className="p-col-12">
          <label className="field-label" htmlFor="assessmentTemplate">
            <FormattedMessage
              id="attrs.assessmentTemplate"
              defaultMessage="Assessment Template"
              htmlFor="assessmentTemplate"
              tagName="label"
            />
          </label>
          <Field
            id="assessmentTemplate"
            name="assessmentTemplate"
            component={DropdownWithValidation}
            options={templates}
            onChange={(event) => {
              setFieldValue('assessmentTemplate', event.value);
              setFieldValue('jobProfile', null);
              changeAsmtTemplate(event.value);
            }}
          />
        </div>
        <div className="p-col-12">
          <label className="field-label" htmlFor="jobProfile">
            <FormattedMessage
              id="attrs.jobName"
              defaultMessage="Job Profile Name"
              htmlFor="jobProfile"
              tagName="label"
            />
          </label>
          <Field
            id="jobProfile"
            name="jobProfile"
            component={renderAsyncSelector}
            processing={jobSearching}
            data={jobs}
            filter
            valueField="id"
            textField="name"
            onSearch={searchJob}
            setFieldValue={(field, selectedJob) => {
              changeURLParams(field, selectedJob.id);
              setFieldValue(field, selectedJob.id);
            }}
            placeholder={`- ${formatMessage(messages.choose)} -`}
          />
        </div>
        <div className="p-col-12">
          <FormattedMessage id="data.export.accounts.label" defaultMessage="Select account(s)">
            {(text) => <label className="field-label">{text}</label>}
          </FormattedMessage>
          <FormattedMessage id="select" defaultMessage="Select">
            {(text) => (
              <Field
                name="accountIds"
                component={FormikMultiSelect}
                options={accounts}
                filter
                filterBy="label,value"
                onChange={(event) => {
                  setFieldValue('accountIds', event.value);
                  changeURLParams('accountIds', event.value);
                }}
                placeholder={`-- ${text} --`}
              />
            )}
          </FormattedMessage>
        </div>
        <div className="p-col-12">
          <label className="field-label" htmlFor="startDate">
            <FormattedMessage
              id="placeholder.startWith"
              defaultMessage="Start date"
              htmlFor="startDate"
              tagName="label"
            />
          </label>
          <Field
            name="startDate"
            id="startDate"
            component={FormikCalendar}
            dateFormat="yy-M-dd"
            onChange={(event) => {
              changeURLDateParams('startDate', event.value);
              setFieldValue('startDate', event.value);
            }}
            placeholder={startDateLabel}
            showButtonBar
          />
        </div>
        <div className="p-col-12">
          <label className="field-label" htmlFor="endDate">
            <FormattedMessage id="placeholder.endWith" defaultMessage="End date" htmlFor="endDate" tagName="label" />
          </label>
          <Field
            name="endDate"
            id="endDate"
            component={FormikCalendar}
            dateFormat="yy-M-dd"
            onChange={(event) => {
              changeURLDateParams('endDate', event.value);
              setFieldValue('endDate', event.value);
            }}
            placeholder={endDateLabel}
            showButtonBar
          />
        </div>
        <div className="p-col-12">
          <label className="field-label" htmlFor="respondentType">
            <FormattedMessage
              id="export.respondent.type"
              defaultMessage="Respondent Type"
              htmlFor="respondentType"
              tagName="label"
            />
          </label>
          <Field
            id="respondentType"
            name="respondentType"
            component={DropdownWithValidation}
            options={RESPONDENT_TYPES}
            onChange={(event) => {
              changeURLParams('respondentType', event.value);
              setFieldValue('respondentType', event.value);
            }}
          />
        </div>
        <div className="p-col-12">
          <FormattedMessage
            id="export.assessments.scored"
            defaultMessage="Only scored Assessments"
            htmlFor="onlyScored"
            tagName="label"
          >
            {(text) => (
              <Field
                id="onlyScored"
                name="onlyScored"
                component={FormikCheckbox}
                setFieldValue={setFieldValue}
                labelText={text}
                readOnly={subscriptionIsExpired}
              />
            )}
          </FormattedMessage>
        </div>
        <div className="p-col-12">
          <FormattedMessage
            id="export.assessments.include-interests-scores"
            defaultMessage="Include Interest Scores"
            htmlFor="withInterestsScores"
            tagName="label"
          >
            {(text) => (
              <Field
                id="withInterestsScores"
                name="withInterestsScores"
                component={FormikCheckbox}
                setFieldValue={setFieldValue}
                labelText={text}
              />
            )}
          </FormattedMessage>
        </div>
        <div className="p-col-12">
          <FormattedMessage
            id="export.assessments.include-ari-fields"
            defaultMessage="Include ARI Fields"
            htmlFor="withAriFields"
            tagName="label"
          >
            {(text) => (
              <Field
                id="withAriFields"
                name="withAriFields"
                component={FormikCheckbox}
                setFieldValue={setFieldValue}
                labelText={text}
              />
            )}
          </FormattedMessage>
        </div>
        <div className="p-col-12">
          <label className="field-label" htmlFor="locale">
            <FormattedMessage id="attrs.locale" defaultMessage="Preferred Language" />
          </label>
          <Field
            id="locale"
            name="locale"
            component={FormLanguageSelector}
            options={translatedTags}
            onChange={(event) => {
              changeURLParams('locale', event.value);
              setFieldValue('locale', event.value);
            }}
          />
        </div>
      </div>
      <div className="p-grid">
        <div className="p-col-12">
          <Button
            icon={classNames('pi pi-download', { 'pi pi-spin pi-spinner': isSubmitting })}
            label={formatMessage(messages.generate)}
            type="submit"
            disabled={isSubmitting}
          />
        </div>
      </div>
      <Debug />
    </Form>
  );
};
DataExportForm.propTypes = {
  jobSearching: PropTypes.bool.isRequired,
  templates: PropTypes.arrayOf(PropTypes.any).isRequired,
  jobs: PropTypes.arrayOf(PropTypes.any).isRequired,
  languageList: PropTypes.arrayOf(PropTypes.any).isRequired,
  searchJob: PropTypes.func.isRequired,
  changeURLParams: PropTypes.func.isRequired,
  changeURLDateParams: PropTypes.func.isRequired,
  changeAsmtTemplate: PropTypes.func.isRequired,
};

export default DataExportForm;
